import { Navigate, Route, Routes } from "react-router-dom";
import { Suspense, useEffect } from "react";
import React from "react";
import "./App.css";
import LoaderNext from "./component/LoaderNext.jsx";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "./context/auth.jsx";
import MouseFollower from "./pages/canva/MouseFollower.jsx";
const DocumentBuilder = React.lazy(() => import("./pages/cv/DocumentBuilder.jsx"));
import useAutoLogout from "./hooks/useAutoLogout.js";

// Lazy imports
const Home = React.lazy(() => import("./pages/Home.jsx"));
const Login = React.lazy(() => import("./pages/auth/Login.jsx"));
const Registration = React.lazy(() => import("./pages/auth/Registration.jsx"));
const Forgot = React.lazy(() => import("./pages/auth/Forgot.jsx"));
const Company = React.lazy(() => import("./pages/Company.jsx"));
const Services = React.lazy(() => import("./pages/Services.jsx"));
const Resources = React.lazy(() => import("./pages/Resources.jsx"));
const CareersPage = React.lazy(() => import("./pages/careers/CareersPage.jsx"));
const JobDetails = React.lazy(() => import("./pages/careers/JobDetails.jsx"));
const BlogPage = React.lazy(() => import("./pages/BlogPage.jsx"));
const BlogPageDetails = React.lazy(() => import("./pages/BlogPageDetails.jsx"));
const ReviewsPage = React.lazy(() => import("./pages/ReviewsPage.jsx"));
const ContactUsPage = React.lazy(() => import("./pages/ContactUsPage.jsx"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage.jsx"));
const TermsOfServicePage = React.lazy(() => import("./pages/TermsOfServicePage.jsx"));
const SupportPage = React.lazy(() => import("./pages/SupportPage.jsx"));
const PortfolioPage = React.lazy(() => import("./pages/PortfolioPage.jsx"));
const PortfolioDetails = React.lazy(() => import("./pages/PortfolioDetails.jsx"));
const PricingPage = React.lazy(() => import("./pages/PricingPage.jsx"));
const PricingPageDetails = React.lazy(() => import("./pages/PricingPageDetails.jsx"));
const Team = React.lazy(() => import("./pages/Team.jsx"));
const TeamMemberDetails = React.lazy(() => import("./pages/TeamMemberDetails.jsx"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail.jsx"));
const UsePrivateRoute = React.lazy(() => import("./component/Routes/UsePrivateRoute.jsx"));
const UserDashboard = React.lazy(() => import("./pages/usersDashboard/user/UserDashboard.jsx"));
const EditProfile = React.lazy(() => import("./pages/usersDashboard/user/EditProfile.jsx"));
const MyOrders = React.lazy(() => import("./pages/usersDashboard/user/MyOrders.jsx"));
const UserApplication = React.lazy(() => import("./pages/usersDashboard/user/UserApplication.jsx"));
const UserBlogPost = React.lazy(() => import("./pages/usersDashboard/user/UserBlogPost.jsx"));

const AdminPrivateRoute = React.lazy(() => import("./component/Routes/AdminPrivateRoute.jsx"));
const AdminDashboard = React.lazy(() => import("./pages/usersDashboard/admin/AdminDashboard.jsx"));
const AdminEditProfile = React.lazy(() => import("./pages/usersDashboard/admin/AdminEditProfile.jsx"));
const AdminSubscription = React.lazy(() => import("./pages/usersDashboard/admin/AdminSubscription.jsx"));
const AdminContactUs = React.lazy(() => import("./pages/usersDashboard/admin/AdminContactUs.jsx"));
const AdminJobPostForm = React.lazy(() => import("./pages/usersDashboard/admin/AdminJobPostForm.jsx"));
const AdminCareerPosts = React.lazy(() => import("./pages/usersDashboard/admin/AdminCareerPosts.jsx"));
const AdminApplication = React.lazy(() => import("./pages/usersDashboard/admin/AdminApplication.jsx"));
const AdminFaqPosts = React.lazy(() => import("./pages/usersDashboard/admin/AdminFaqPosts.jsx"));
const AdminService = React.lazy(() => import("./pages/usersDashboard/admin/AdminService.jsx"));
const AdminPricing = React.lazy(() => import("./pages/usersDashboard/admin/AdminPricing.jsx"));
const AdminTeamMember = React.lazy(() => import("./pages/usersDashboard/admin/AdminTeamMember.jsx"));
const AdminProject = React.lazy(() => import("./pages/usersDashboard/admin/AdminProject.jsx"));
const AdminBlogPosts = React.lazy(() => import("./pages/usersDashboard/admin/AdminBlogPosts.jsx"));
const AdminUsersList = React.lazy(() => import("./pages/usersDashboard/admin/AdminUsersList.jsx"));
const AdminOrderList = React.lazy(() => import("./pages/usersDashboard/admin/AdminOrderList.jsx"));
const AdminInvoices = React.lazy(() => import("./pages/usersDashboard/admin/AdminInvoices.jsx"));

const WebDevelopmentDetails = React.lazy(() => import("./pages/services/WebDevelopmentDetails.jsx"));
const AndroidDevelopmentDetails = React.lazy(() => import("./pages/services/AndroidDevelopmentDetails.jsx"));
const UIDesignDetails = React.lazy(() => import("./pages/services/UIDesignDetails.jsx"));
const DigitalMarketingDetails = React.lazy(() => import("./pages/services/DigitalMarketingDetails.jsx"));

const Error404 = React.lazy(() => import("./pages/Error404.jsx"));
const UnderConstruction = React.lazy(() => import("./pages/canva/UnderConstruction.jsx"));
const CartPage = React.lazy(() => import("./pages/cart/CartPage.jsx"));

function App() {
  const [auth] = useAuth();

  // 1-hour session auto-logout + 5-min warning toast
  useAutoLogout();

  useEffect(() => {
    AOS.init({ once: true, duration: 600 });
  }, []);

  return (
    <>
      {/* MouseFollower stays outside Suspense so it always renders */}
      <MouseFollower />
      <Suspense fallback={<LoaderNext />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/login"
            element={auth?.user ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/registration"
            element={auth?.user ? <Navigate to="/" replace /> : <Registration />}
          />
          <Route
            path="/forgot"
            element={auth?.user ? <Navigate to="/" replace /> : <Forgot />}
          />

          <Route path="/document-builder" element={<DocumentBuilder />} />

          {/* User private routes */}
          <Route path="/dashboard" element={<UsePrivateRoute />}>
            <Route path="user" element={<UserDashboard />} />
            <Route path="user/edit-profile" element={<EditProfile />} />
            <Route path="user/my-orders" element={<MyOrders />} />
            <Route path="user/user-application" element={<UserApplication />} />
            <Route path="user/user-blog-post" element={<UserBlogPost />} />
          </Route>

          {/* Admin private routes */}
          <Route path="/dashboard" element={<AdminPrivateRoute />}>
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="admin/edit-profile" element={<AdminEditProfile />} />
            <Route path="admin/subscription" element={<AdminSubscription />} />
            <Route path="admin/contact-us" element={<AdminContactUs />} />
            <Route path="admin/jobs/create" element={<AdminJobPostForm />} />
            <Route path="admin/career-posts" element={<AdminCareerPosts />} />
            <Route path="admin/applications" element={<AdminApplication />} />
            <Route path="admin/faq-posts" element={<AdminFaqPosts />} />
            <Route path="admin/services-post" element={<AdminService />} />
            <Route path="admin/pricing" element={<AdminPricing />} />
            <Route path="admin/team-member" element={<AdminTeamMember />} />
            <Route path="admin/project" element={<AdminProject />} />
            <Route path="admin/blog-posts" element={<AdminBlogPosts />} />
            <Route path="admin/users-list" element={<AdminUsersList />} />
            <Route path="admin/order-list" element={<AdminOrderList />} />
            <Route path="admin/invoices" element={<AdminInvoices />} />
          </Route>

          {/* Public pages */}
          <Route path="/company" element={<Company />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:id" element={<Services />} />
          <Route path="/services/web-development" element={<WebDevelopmentDetails />} />
          <Route path="/services/app-development" element={<AndroidDevelopmentDetails />} />
          <Route path="/services/uiux-design" element={<UIDesignDetails />} />
          <Route path="/services/degital-marketing" element={<DigitalMarketingDetails />} />

          <Route path="/resources" element={<Resources />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:slug" element={<JobDetails />} />

          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog-page-details/:slug" element={<BlogPageDetails />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/support" element={<SupportPage />} />

          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:id" element={<PortfolioDetails />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing/:id" element={<PricingPageDetails />} />
          <Route path="/team" element={<Team />} />
          <Route path="/team/:id" element={<TeamMemberDetails />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/cart" element={<CartPage />} />

          {/* Errors */}
          <Route path="/under-construction" element={<UnderConstruction />} />
          <Route path="*" element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
